import { getToken } from './storage';
import { AUTH0_DOMAIN, AUTH0_CLIENT_ID, AUTH0_AUDIENCE } from '../config/auth0';

// Configuration pour le refresh token
const EXPIRY_BUFFER_MS = 5 * 60 * 1000; // 5 minutes de marge avant expiration
const REFRESH_IN_PROGRESS_KEY = 'token_refresh_in_progress';

// Fonction pour décoder un token JWT sans bibliothèque externe
export function decodeJWT(token: string): any {
  try {
    // Vérifier si le token est bien formé avant de tenter de le décoder
    if (!token || typeof token !== 'string' || !token.includes('.')) {
      console.error('Token JWT invalide ou mal formaté');
      return null;
    }
    
    const tokenParts = token.split('.');
    if (tokenParts.length !== 3) {
      console.error('Format JWT invalide: un JWT valide doit avoir 3 parties');
      return null;
    }
    
    const base64Url = tokenParts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    // base64url omet le padding '=' — window.atob() en a besoin
    const padded = base64 + '='.repeat((4 - base64.length % 4) % 4);
    
    try {
      const jsonPayload = decodeURIComponent(
        window.atob(padded)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (atobError) {
      console.error('Erreur lors du décodage base64 du token:', atobError);
      return null;
    }
  } catch (e) {
    console.error('Erreur de décodage du token JWT:', e);
    return null;
  }
}

// Vérifier si le token est proche de l'expiration
export function isTokenExpiringSoon(token: string): boolean {
  if (!token) return true;
  
  const decodedToken = decodeJWT(token);
  if (!decodedToken || !decodedToken.exp) return true;
  
  const expiryTime = decodedToken.exp * 1000; // Convertir en millisecondes
  const currentTime = Date.now();
  
  return expiryTime - currentTime < EXPIRY_BUFFER_MS;
}

// Récupérer un nouveau token via le refresh token
export async function refreshToken(): Promise<string | null> {
  // Éviter les appels multiples simultanés
  if (localStorage.getItem(REFRESH_IN_PROGRESS_KEY) === 'true') {
    // Attendre que le rafraîchissement en cours se termine avec timeout
    return new Promise((resolve) => {
      let attempts = 0;
      const maxAttempts = 25; // 5 secondes max (25 * 200ms)
      
      const checkInterval = setInterval(() => {
        attempts++;
        if (localStorage.getItem(REFRESH_IN_PROGRESS_KEY) !== 'true' || attempts >= maxAttempts) {
          clearInterval(checkInterval);
          if (attempts >= maxAttempts) {
            console.warn('Timeout lors de l\'attente du refresh token');
            // Forcer le nettoyage du flag en cas de timeout
            localStorage.removeItem(REFRESH_IN_PROGRESS_KEY);
          }
          resolve(getToken());
        }
      }, 200);
    });
  }
  
  try {
    localStorage.setItem(REFRESH_IN_PROGRESS_KEY, 'true');
    
    // Récupérer le refresh token
    const refreshToken = localStorage.getItem('auth0_refresh_token');
    if (!refreshToken) {
      console.warn('Pas de refresh token disponible');
      return null;
    }
    
    // Faire la requête de rafraîchissement vers Auth0
    const response = await fetch(`https://${AUTH0_DOMAIN}/oauth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        grant_type: 'refresh_token',
        client_id: AUTH0_CLIENT_ID,
        refresh_token: refreshToken,
        audience: AUTH0_AUDIENCE // 🎯 Utiliser l'audience configurée pour forcer un JWT
      })
    });
    
    const data = await response.json();
    if (!response.ok) {
      console.error('Échec du rafraîchissement du token:', data);
      return null;
    }
    
    // Stocker le nouveau token
    localStorage.setItem('auth0_token', data.access_token);
    if (data.refresh_token) {
      localStorage.setItem('auth0_refresh_token', data.refresh_token);
    }
    
    console.log('Token rafraîchi avec succès');
    return data.access_token;
  } catch (error) {
    console.error('Erreur lors du rafraîchissement du token:', error);
    return null;
  } finally {
    localStorage.removeItem(REFRESH_IN_PROGRESS_KEY);
  }
}

// Vérifier et rafraîchir le token si nécessaire
export async function ensureFreshToken(): Promise<string | null> {
  const token = getToken();
  
  if (!token) {
    console.warn('Pas de token disponible');
    return null;
  }
  
  if (isTokenExpiringSoon(token)) {
    console.log('Token expirant bientôt, rafraîchissement...');
    return refreshToken();
  }
  
  return token;
}
